// Off-canvas slide-in drawer for mobile/tablet (below lg:). Uses Radix Dialog
// primitives for a11y (focus trap + escape-to-close + aria-modal) but visually
// presents as a left-edge drawer, not a centered modal.
//
// Trigger is the hamburger icon (top-left of the AppShell mobile/tablet
// header). The SkedMark logo is rendered separately by AppShell on the
// top-right as a brand anchor (Link back to /) — not a drawer trigger.

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { logout } from "@/lib/api";
import { NavTree, SkedCrossSell } from "./SidebarNav";

interface MobileNavProps {
  property?: {
    propertyName: string;
    accountName: string | null;
  } | null;
}

export function MobileNav({ property }: MobileNavProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await logout();
      setOpen(false);
      navigate("/");
    } catch {
      setSigningOut(false);
    }
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label="Open menu"
          className="-ml-2 inline-flex size-10 items-center justify-start rounded-md pl-0 pr-2 text-neutral-700 transition-colors hover:bg-neutral-50 hover:text-neutral-950 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2 lg:hidden"
        >
          <i className="fa-solid fa-bars text-base" aria-hidden="true" />
        </button>
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-40 bg-neutral-950/40",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
            "duration-200",
          )}
        />
        <DialogPrimitive.Content
          aria-label="Primary navigation"
          className={cn(
            "fixed inset-y-0 left-0 z-50 flex h-dvh w-[280px] flex-col gap-4 overflow-y-auto bg-white px-3 py-4 shadow-xl",
            "data-[state=open]:animate-in data-[state=open]:slide-in-from-left",
            "data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left",
            "duration-300",
          )}
        >
          <DialogPrimitive.Title className="sr-only">Primary navigation</DialogPrimitive.Title>
          <div className="flex justify-end px-1">
            <DialogPrimitive.Close asChild>
              <button
                type="button"
                aria-label="Close menu"
                className="inline-flex size-8 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-50 hover:text-neutral-950 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
              >
                <i className="fa-regular fa-xmark text-base" aria-hidden="true" />
              </button>
            </DialogPrimitive.Close>
          </div>

          {property ? (
            <section
              aria-label="Connected GA4 property"
              className="rounded-xl bg-neutral-25 p-3 ring-1 ring-neutral-950/5"
            >
              <p className="text-[11px] font-bold uppercase tracking-wide text-purple-700">
                Property
              </p>
              <p className="mt-1 truncate font-display text-sm font-semibold text-neutral-950">
                {property.propertyName}
              </p>
              {property.accountName ? (
                <p className="truncate text-sm text-neutral-500">{property.accountName}</p>
              ) : null}
              <Link
                to="/app/properties"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex items-center gap-1 rounded text-sm font-semibold text-purple-700 hover:text-purple-900 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
              >
                Change property
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </section>
          ) : null}

          <NavTree onNavigate={() => setOpen(false)} />

          <div className="mt-auto flex flex-col gap-3">
            <SkedCrossSell />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleSignOut}
              disabled={signingOut}
              className="justify-start gap-3 px-3"
            >
              <i
                className="fa-regular fa-arrow-right-from-bracket w-4 text-center text-sm"
                aria-hidden="true"
              />
              {signingOut ? "Signing out…" : "Sign out"}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
